"use client";

import { Mail } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Financialfaq() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    value: "item-1",
    question: "What is included in a revenue audit?",
    answer: "Our comprehensive revenue audit evaluates your current OTA performance, pricing strategies, and tech stack efficiency. We identify missed revenue opportunities and provide actionable insights to optimize your property's market positioning and profitability."
  }, {
    value: "item-2",
    question: "How long does the consulting process take?",
    answer: "The duration depends on the scope of your needs. A standard revenue audit typically takes 1–2 weeks, while ongoing revenue management and tech implementation are structured as long-term partnerships to ensure sustained growth."
  }, {
    value: "item-3",
    question: "Do I need to change my current booking engine?",
    answer: "Not necessarily. We first assess your existing tech stack to see if it can be optimized. If a change is required, we recommend solutions that integrate seamlessly with your operations to maximize direct bookings and revenue."
  }, {
    value: "item-4",
    question: "What is your cancellation policy for consulting?",
    answer: "We require a 48-hour notice for rescheduling any strategy sessions. Our service agreements are designed to be flexible, and we prioritize clear communication to ensure our partnership aligns with your business goals."
  }, {
    value: "item-5",
    question: "Are remote consulting services available?",
    answer: "Yes. We provide remote revenue management and consulting services for hotels globally. We utilize secure digital platforms to collaborate with your team, ensuring you receive expert guidance regardless of your property's location."
  }, {
    value: "item-6",
    question: "Can I hire you for a one-time project?",
    answer: "Absolutely. Whether you need a one-time revenue audit, help with OTA listing optimization, or guidance on a specific tech implementation, we offer project-based consulting tailored to your immediate requirements."
  }, {
    value: "item-7",
    question: "Which hotel types do you work with?",
    answer: "We work with a diverse range of properties, including boutique hotels, independent resorts, and city-center properties. Our strategies are customized to fit the unique demand patterns and market segments of your specific hotel."
  }, {
    value: "item-8",
    question: "How soon will I see revenue improvements?",
    answer: "While results vary based on market conditions, many clients see improvements in OTA visibility and conversion rates within the first 30 days of implementing our data-driven strategies."
  }];
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
  return <section data-section-id="3156" id="financialfaq" className="bg-background py-20 md:py-[120px] text-foreground">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={leftRef} className={`lg:col-span-4 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="lg:sticky lg:top-24">
              <p className="font-default text-xs tracking-[0.25em] uppercase text-secondary mb-6">
                Frequently Asked
              </p>
              <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.05] text-foreground mb-6">
                Expert <span className="italic font-light">Insights.</span>
              </h2>
              <p className="font-default text-base leading-relaxed text-muted-foreground mb-8 max-w-md">
                Answers to common questions about our revenue management and consulting services. Let us help you maximize your property's potential.
              </p>
              
              <div className="flex flex-col gap-4 mb-8">
                <Link to="/OurServicesPage">
                  <Button variant="outline" className="w-full justify-start rounded-lg">
                    View All Services
                  </Button>
                </Link>
                <Link to="/BlogPage">
                  <Button variant="outline" className="w-full justify-start rounded-lg">
                    Read Revenue Tips
                  </Button>
                </Link>
                <Link to="/ContactPage">
                  <Button variant="secondary" className="w-full justify-start rounded-lg">
                    Schedule Consultation
                  </Button>
                </Link>
              </div>

              <Link to="/ContactPage" className="inline-block">
                <Button variant="ghost" className="group h-auto px-0 py-2 font-default text-sm tracking-wide text-foreground hover:bg-transparent hover:text-secondary">
                  <Mail className="size-4 stroke-[1.25] mr-2" />
                  <span>Contact our team</span>
                  <span className="ml-1 transition-transform duration-500 ease-out group-hover:translate-x-1">
                    →
                  </span>
                </Button>
              </Link>
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-8 transition-all duration-1000 delay-150 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="space-y-12">
              {faqs.map((faq, i) => <div key={faq.value} data-index={i} className="border-b border-border pb-8 last:border-0">
                  <h3 className="font-default font-semibold text-xl md:text-[1.4rem] leading-snug text-foreground mb-4">
                    {faq.question}
                  </h3>
                  <p className="font-default text-base leading-[1.7] text-muted-foreground">
                    {faq.answer}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </div>
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} />
    </section>;
}